import { useQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';

import { _getEntityRoleMembers } from '@/api/entity-roles/queries';
import { _getEntityViewsTeamAssociates } from '@/api/entity-views/queries';
import { useCreateRoleRequest } from '@/api/role-requests/mutations';
import { type EntityRoleRequest, RoleRequestType } from '@/api/role-requests/types';
import { useGetUserProfile } from '@/api/user-dashboard/queries';
import UserPlusIcon from '@/assets/svg/ui-elements/user-plus.svg';
import { Button, EmptyState, Loader, theme, Tooltip, Typography } from '@/shared/design-system';
import {
  matchesMember,
  MEMBER_SEARCH_THRESHOLD,
  MemberIdentityView,
  MemberSearch,
  PendingMemberRows,
  RemoveMemberDialog,
} from '@/shared/modules/role-board/components/member-list';
import { pendingMemberChanges, unavailableMemberIds } from '@/utils/role-request-activity';

import { buildTeamMemberLookup, resolveTeamMember } from '../resolve-team-member';
import { CenteredState, MemberActions, MemberList, MemberRow } from '../styled';
import { useRoleAdminAccess } from '../use-role-admin-access';
import { AddMembersModal } from './add-members-modal';
import { MemberListFooter } from './role-members-tab.styled';

export const NO_MEMBERS_MESSAGE = 'Nobody holds this role yet';
export const LOAD_MEMBERS_ERROR = "Couldn't load this role's members";
export const OWNER_REMOVE_TOOLTIP = 'Only this member can remove themself from the Owner role.';
export const LAST_OWNER_TOOLTIP =
  'The entity must keep one Owner. Give the role to someone else before giving it up.';
export const LAST_MEMBER_TOOLTIP =
  'You are this role’s only member, so you cannot remove yourself from it. Add someone else first, or ask an administrator to remove it for you.';

/** Enough of the team to resolve almost any role's members without a second page. */
const TEAM_LOOKUP_PAGE_SIZE = 100;

export type RoleMembersTabProps = {
  entityId?: string | null;
  roleId: string;
  roleName: string;
  /** The Owner role can only be removed from a member by that member themself. */
  isOwnerRole?: boolean;
  /** Requests still waiting for a decision, so joins and removals in flight are visible. */
  pendingRequests?: readonly EntityRoleRequest[];
};

export const NO_MEMBER_MATCHES_MESSAGE = 'No members match that search';

export const RoleMembersTab: FC<RoleMembersTabProps> = ({
  entityId,
  roleId,
  roleName,
  isOwnerRole = false,
  pendingRequests = [],
}) => {
  const { canAssign } = useRoleAdminAccess();
  const { user: currentUser } = useGetUserProfile();

  const [isAddOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [removing, setRemoving] = useState<{ userId: string; label: string } | null>(null);

  const pendingChanges = useMemo(
    () => pendingMemberChanges(pendingRequests, roleId),
    [pendingRequests, roleId],
  );
  const needle = search.trim().toLowerCase();

  const members = useQuery(_getEntityRoleMembers(entityId, roleId));
  const team = useQuery(
    _getEntityViewsTeamAssociates({ id: entityId ?? null, limit: TEAM_LOOKUP_PAGE_SIZE }),
  );
  const teamLookup = useMemo(() => buildTeamMemberLookup(team.data), [team.data]);

  const { mutate: requestRoleChange, isPending: isRemoving } = useCreateRoleRequest({ entityId });

  // `isPending`, not `isLoading` — see the note in `role-permissions-tab.tsx`.
  if (members.isPending) {
    return (
      <CenteredState>
        <Loader />
      </CenteredState>
    );
  }

  if (members.isError || !members.data) {
    return (
      <CenteredState>
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {LOAD_MEMBERS_ERROR}
        </Typography>
      </CenteredState>
    );
  }

  const addMembersButton = canAssign && (
    <MemberListFooter>
      <Button size="md" variant="solid" color="primary" onClick={() => setAddOpen(true)}>
        <UserPlusIcon aria-hidden />
        Add members
      </Button>
    </MemberListFooter>
  );

  const resolvedMembers = members.data.members.map(({ userId }) => ({
    userId,
    resolved: resolveTeamMember(teamLookup, userId),
  }));
  const visibleMembers = resolvedMembers.filter(({ userId, resolved }) =>
    matchesMember(needle, resolved.name, resolved.email, userId),
  );

  return (
    <>
      {members.data.members.length >= MEMBER_SEARCH_THRESHOLD && (
        <MemberSearch value={search} onChange={setSearch} />
      )}

      <PendingMemberRows changes={pendingChanges} />

      {members.data.members.length === 0 ? (
        <EmptyState title={NO_MEMBERS_MESSAGE} />
      ) : (
        <MemberList>
          {needle && !visibleMembers.length && (
            <Typography variant="body3" color={theme.colors.text.secondary}>
              {NO_MEMBER_MATCHES_MESSAGE}
            </Typography>
          )}
          {visibleMembers.map(({ userId, resolved }) => {
            // The Owner role can only be removed from a member by that member
            // themself (`ENTITY.ROLE.OWNER_PROTECTED` otherwise) — every other
            // member's row renders the control disabled rather than hiding it, so
            // it stays visible that Owner behaves differently.
            // This list IS the role's members, so one entry means the row is the last of them.
            const isSelf = userId === currentUser?.id;
            const isSoleMember = members.data.members.length <= 1;
            const removeBlockedByOwnerRule = isOwnerRole && !isSelf;
            // `ENTITY.ROLE.LAST_ADMIN_PROTECTED`: the last Owner cannot give the role up, not even
            // to themself, and unlike the rule below that holds whoever is asking.
            const removeBlockedByLastOwnerRule = isOwnerRole && isSoleMember;
            // `ENTITY.ROLE_REQUEST.LAST_ROLE_MEMBER_PROTECTED` (GP-4030): nobody empties a role by
            // their own hand. An administrator taking the last member off is still allowed.
            const removeBlockedByLastMemberRule = isSelf && isSoleMember;
            const pendingRemoval = pendingChanges.find(
              (change) => change.userId === userId && !change.assign,
            );
            const removeBlockedReason = pendingRemoval
              ? 'A request to remove this member is already waiting for approval.'
              : removeBlockedByLastOwnerRule
                ? LAST_OWNER_TOOLTIP
                : removeBlockedByLastMemberRule
                  ? LAST_MEMBER_TOOLTIP
                  : removeBlockedByOwnerRule
                    ? OWNER_REMOVE_TOOLTIP
                    : '';

            const removeButton = (
              <Button
                size="sm"
                variant="ghost"
                color="destructive"
                disabled={isRemoving || !!removeBlockedReason}
                onClick={() => setRemoving({ userId, label: resolved.name })}
              >
                Remove
              </Button>
            );

            return (
              <MemberRow key={userId}>
                <MemberIdentityView
                  primary={resolved.name}
                  secondary={resolved.email}
                  isSelf={isSelf}
                  pendingRemoval={pendingRemoval}
                />

                <MemberActions>
                  {canAssign &&
                    (removeBlockedReason ? (
                      // A disabled button emits no pointer events, so the tooltip
                      // hangs off a wrapper that does.
                      <Tooltip title={removeBlockedReason}>
                        <span>{removeButton}</span>
                      </Tooltip>
                    ) : (
                      removeButton
                    ))}
                </MemberActions>
              </MemberRow>
            );
          })}
        </MemberList>
      )}

      {addMembersButton}

      <RemoveMemberDialog
        open={removing !== null}
        memberLabel={removing?.label ?? ''}
        roleName={roleName}
        isPending={isRemoving}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (!removing) return;

          requestRoleChange(
            { type: RoleRequestType.RemoveRole, payload: { roleId, userId: removing.userId } },
            { onSettled: () => setRemoving(null) },
          );
        }}
      />

      <AddMembersModal
        open={isAddOpen}
        onClose={() => setAddOpen(false)}
        entityId={entityId}
        roleId={roleId}
        roleName={roleName}
        existingMemberIds={unavailableMemberIds(
          members.data.members.map((member) => member.userId),
          pendingChanges,
        )}
      />
    </>
  );
};
